import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../Api/Client.js";
import { Alert, Button, Checkbox, Field, Textarea } from "../../Ui/index.jsx";
import "../../Styles/ProfileScreen.css";

const POLICY_PATH = "/docs/company/ResignationProcess.md";

const when = (iso) => (iso
  ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  : "");

// Resigning, on purpose a deliberate two-step: the button only opens the form;
// nothing is sent until the person has said why and ticked that they have read
// the process. The notice period is NOT stated here: it is whatever the person's
// own offer letter says, and the manager sets the last working day.
export default function ResignationSection({ navigate }) {
  const [resignation, setResignation] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // A failed status check is not the same as "has not resigned": the form stays hidden until we know.
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoadFailed(false);
    apiGet("/Users/EmployeeProfiles/my-resignation/")
      .then((res) => setResignation(res?.resignation || null))
      .catch(() => setLoadFailed(true));
  }, [attempt]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiPost("/Users/EmployeeProfiles/resign/", { reason, acknowledged });
      setResignation(res?.resignation || null);
      setOpen(false);
    } catch (err) {
      setError(err?.payload?.detail || err?.data?.detail || err?.message || "Could not submit your resignation.");
    } finally {
      setBusy(false);
    }
  };

  const policy = (
    <Button variant="link" onClick={() => navigate && navigate(POLICY_PATH)}>resignation process</Button>
  );

  if (loadFailed) {
    return (
      <div className="Resign">
        <Alert tone="danger">We could not check your resignation status.</Alert>
        <div className="Resign-Actions">
          <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
        </div>
      </div>
    );
  }

  if (resignation === undefined) return <p className="ds-small">Loading…</p>;

  if (resignation && resignation.status === "Submitted") {
    return (
      <div className="Resign">
        <Alert><strong>Resignation submitted on {when(resignation.requested_on)}.</strong> Your manager and HR have been told. Your
          manager will agree your last working day with you, based on the notice period in your offer letter.</Alert>
        <p className="ds-small">Until you are relieved, your work and reporting continue as normal. See the {policy}.</p>
      </div>
    );
  }

  if (resignation && resignation.status === "Approved") {
    return (
      <div className="Resign">
        <Alert tone="success"><strong>Your resignation has been approved.</strong>{resignation.last_working_day ? ` Your last working day is ${when(resignation.last_working_day)}.` : ""}</Alert>
        <p className="ds-small">See the {policy} for the handover and final settlement steps.</p>
      </div>
    );
  }

  return (
    <div className="Resign">
      <p className="ds-card__sub">
        Your notice period and the terms of leaving are those in your offer letter. Read the {policy} first; it sets out the
        steps and quotes the terms. Your manager and HR are told as soon as you resign.
      </p>
      {!open ? (
        <div className="Resign-Actions">
          <Button variant="danger" onClick={() => setOpen(true)}>Resign</Button>
        </div>
      ) : (
        <div className="Resign-Form">
          <Field label="Why are you leaving?">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A sentence or two is enough" />
          </Field>
          <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} label="I have read the resignation process and the leaving terms in my offer letter." />
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="Resign-Actions">
            <Button variant="danger" onClick={submit}
              disabled={busy || !acknowledged || reason.trim().length < 10}>{busy ? "Submitting…" : "Submit my resignation"}</Button>
            <Button onClick={() => { setOpen(false); setError(""); }} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
